import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
  "août", "septembre", "octobre", "novembre", "décembre"];
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatJour(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long",
    });
  } catch { return iso; }
}

export default function Calendrier() {
  const { user } = useAuth();
  const today = new Date();
  const [curMonth, setCurMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [collectes, setCollectes] = useState([]);
  const [ouvertures, setOuvertures] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  // Préférences locales du visiteur
  const [showCollectes, setShowCollectes] = useState(() => {
    return localStorage.getItem("calendrier_show_collectes") !== "0";
  });
  const [zone, setZone] = useState(() => {
    return localStorage.getItem("calendrier_zone") || "C";
  });
  useEffect(() => {
    localStorage.setItem("calendrier_show_collectes", showCollectes ? "1" : "0");
  }, [showCollectes]);
  useEffect(() => {
    localStorage.setItem("calendrier_zone", zone);
  }, [zone]);

  const moisStr = `${curMonth.getFullYear()}-${String(curMonth.getMonth() + 1).padStart(2, "0")}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, o] = await Promise.all([
        api.get(`/evenements?mois=${moisStr}`),
        api.get(`/collectes?zone=${zone}&mois=${moisStr}`).catch(() => ({ items: [] })),
        api.get(`/calendrier/ouvertures?mois=${moisStr}`).catch(() => ({ items: [] })),
      ]);
      setEvents(d.items || []);
      setCollectes(c.items || []);
      setOuvertures(o.items || []);
    } catch {
      setEvents([]);
      setCollectes([]);
      setOuvertures([]);
    } finally {
      setLoading(false);
    }
  }, [moisStr, zone]);

  useEffect(() => { load(); }, [load]);

  const eventsByDay = {};
  events.forEach((e) => {
    const start = e.date_debut;
    const end = e.date_fin || e.date_debut;
    let d = new Date(start + "T00:00:00");
    const last = new Date(end + "T00:00:00");
    while (d <= last) {
      const key = ymd(d);
      if (key.startsWith(moisStr)) (eventsByDay[key] = eventsByDay[key] || []).push(e);
      d.setDate(d.getDate() + 1);
    }
  });

  const collectesByDay = {};
  if (showCollectes) {
    collectes.forEach((c) => { collectesByDay[c.date] = c; });
  }

  const ouverturesByDay = {};
  ouvertures.forEach((o) => {
    (ouverturesByDay[o.date] = ouverturesByDay[o.date] || []).push(o);
  });

  const firstDay = new Date(curMonth.getFullYear(), curMonth.getMonth(), 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(curMonth.getFullYear(), curMonth.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);

  const prevMonth = () => setCurMonth(new Date(curMonth.getFullYear(), curMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurMonth(new Date(curMonth.getFullYear(), curMonth.getMonth() + 1, 1));

  const toggleInscription = async (e) => {
    try {
      if (e.est_inscrit) await api.del(`/evenements/${e.id}/inscription`);
      else await api.post(`/evenements/${e.id}/inscription`, {});
      await load();
      const fresh = await api.get(`/evenements/${e.id}`);
      setSelected(fresh);
    } catch (err) {
      alert(err.message || "Action impossible");
    }
  };

  // [lot6] inscription anonyme (evenements ouverts a tous)
  const [anonNom, setAnonNom] = useState("");
  const [anonEmail, setAnonEmail] = useState("");
  const [anonDone, setAnonDone] = useState(false);
  const [anonErr, setAnonErr] = useState("");
  const inscrireAnonyme = async (e) => {
    setAnonErr('');
    if (!anonNom.trim() || !anonEmail.trim() || !anonEmail.includes('@')) {
      setAnonErr('Merci d\'indiquer votre nom et un email valide.');
      return;
    }
    try {
      await api.post(`/evenements/${e.id}/inscription`, { nom: anonNom.trim(), email: anonEmail.trim() });
      setAnonDone(true);
      setAnonNom(""); setAnonEmail("");
      await load();
    } catch (err) {
      setAnonErr(err.message || 'Inscription impossible');
    }
  };

  return (
    <div className="container">
      <h1>Calendrier des événements</h1>
      <p className="muted mb-3">
        Les manifestations et rendez-vous de la commune.
        {!user && " Connectez-vous pour voir les événements qui vous sont réservés."}
      </p>

      <div className="cal-head">
        <button className="secondary small" onClick={prevMonth}>← Mois précédent</button>
        <h2 style={{ margin: 0, textTransform: "capitalize" }}>
          {MOIS[curMonth.getMonth()]} {curMonth.getFullYear()}
        </h2>
        <button className="secondary small" onClick={nextMonth}>Mois suivant →</button>
      </div>

      <div className="cal-collectes-bar">
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
          <input type="checkbox" style={{ width: "auto" }}
                 checked={showCollectes}
                 onChange={(e) => setShowCollectes(e.target.checked)} />
          <span>🗑️ Afficher les collectes des déchets</span>
        </label>
        {showCollectes && (
          <div className="cal-zone-switch">
            <span className="muted" style={{ fontSize: "0.85rem" }}>Zone :</span>
            <button className={`zone-btn ${zone === "C" ? "active" : ""}`}
                    onClick={() => setZone("C")}>C</button>
            <button className={`zone-btn ${zone === "H" ? "active" : ""}`}
                    onClick={() => setZone("H")}>H</button>
            <span className="muted" style={{ fontSize: "0.78rem" }}>
              🟢 sacs verts &nbsp; 🟡 sacs jaunes
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <p className="muted">Chargement…</p>
      ) : (
        <div className="cal-grid">
          {JOURS.map((j) => <div key={j} className="cal-dow">{j}</div>)}
          {cells.map((day, i) => {
            if (day === null) return <div key={`e${i}`} className="cal-cell cal-empty" />;
            const key = `${moisStr}-${String(day).padStart(2, "0")}`;
            const dayEvents = eventsByDay[key] || [];
            const dayOuvertures = ouverturesByDay[key] || [];
            const collecte = collectesByDay[key];
            const isToday = ymd(today) === key;
            return (
              <div key={key} className={`cal-cell ${isToday ? "cal-today" : ""}`}>
                <div className="cal-daynum">{day}</div>
                {collecte && (
                  <div className="cal-collecte" title={collecte.label}>
                    {collecte.emoji} {collecte.type === "V" ? "Sacs verts" : "Sacs jaunes"}
                  </div>
                )}
                {dayOuvertures.map((o, idx) => (
                  <div key={`ouv-${idx}`} className="cal-ouverture"
                       style={{ background: o.couleur, color: "#fff" }}
                       title={`${o.type === "bibliotheque" ? "Bibliothèque" : "Bar associatif"} – ${o.heure_debut} à ${o.heure_fin}`}>
                    {o.emoji} {o.heure_debut}-{o.heure_fin}
                  </div>
                ))}
                {dayEvents.map((e, idx) => (
                  <button key={idx} className="cal-evt"
                          style={{ background: e.couleur || "#1d4f91" }}
                          onClick={() => { setSelected(e); setAnonDone(false); setAnonErr(""); }}>
                    {e.emoji ? `${e.emoji} ` : ""}{e.titre}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(ev) => ev.stopPropagation()}>
            <h2>{selected.emoji ? `${selected.emoji} ` : ""}{selected.titre}</h2>
            <p className="muted">{formatJour(selected.date_debut)}
              {selected.date_fin && selected.date_fin !== selected.date_debut
                ? ` → ${formatJour(selected.date_fin)}` : ""}
              {!selected.toute_la_journee && selected.heure_debut
                ? ` · ${selected.heure_debut}${selected.heure_fin ? "–" + selected.heure_fin : ""}` : ""}
            </p>
            {selected.lieu && <p className="mt-1"><strong>Lieu :</strong> {selected.lieu}</p>}
            {selected.description && (
              <p className="mt-2" style={{ whiteSpace: "pre-wrap" }}>{selected.description}</p>
            )}

            {selected.inscription_active && (
              <div className="mt-3">
                {user ? (
                  <>
                    <p className="muted">{selected.nb_participants} inscrit(s)</p>
                    <button className={selected.est_inscrit ? "secondary" : ""}
                            onClick={() => toggleInscription(selected)}>
                      {selected.est_inscrit ? "Se désinscrire" : "S'inscrire à cet événement"}
                    </button>
                  </>
                ) : selected.inscription_ouverte_tous ? (
                  anonDone ? (
                    <p className="success">✓ Votre inscription est enregistrée. Merci !</p>
                  ) : (
                    <div className="cal-anon-form">
                      <p className="muted">{selected.nb_participants} inscrit(s). Inscription ouverte à tous :</p>
                      {anonErr && <div className="error">{anonErr}</div>}
                      <input type="text" placeholder="Votre nom" value={anonNom}
                             onChange={(ev) => setAnonNom(ev.target.value)} maxLength={160} />
                      <input type="email" placeholder="Votre email" value={anonEmail}
                             onChange={(ev) => setAnonEmail(ev.target.value)} maxLength={200} />
                      <button onClick={() => inscrireAnonyme(selected)}>S'inscrire</button>
                      <p className="muted" style={{ fontSize: "0.78rem", marginTop: "0.4rem" }}>
                        Votre email sert uniquement à vous contacter au sujet de cet événement.
                      </p>
                    </div>
                  )
                ) : (
                  <p className="muted">Connectez-vous pour vous inscrire à cet événement.</p>
                )}
              </div>
            )}
            <div className="modal-actions">
              <button className="secondary" onClick={() => setSelected(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
